<template>
	<div class="order-summary" @keyup.esc="closeAction">
		<modal :modal-title="$t('history.order-summary.title')" :show-modal="showModal" class="order-summary-modal">
			<div v-if="!transactionData" slot="body" :ref="bodySelector" tabindex="0" class="body">
				<div class="spinner-wrapper">
					<span class="spinner-circle"></span>
				</div>
			</div>
			<div v-else slot="body" :ref="bodySelector" tabindex="0" class="body">
				<div class="summary">
					<div class="order-number">
						<span>{{ $t('wallet.order-number') }}</span>
						<span>{{ transactionData.orderNumber }}</span>
					</div>
					<div class="date">
						<span>{{ $t('wallet.date') }}</span>
						<span>{{ new Date(transactionData.date).toLocaleDateString(`${$i18n.locale}-u-ca-gregory`) }}</span>
					</div>
					<div v-if="!isUseMileageAll" class="payment-method">
						<span>{{ $t('history.order-summary.payment-method') }}</span>
						<span>{{ transactionData.paymentMethod }}</span>
					</div>
					<div v-if="isPending" class="order-status">
						<span>{{ $t('history.order-summary.status') }}</span>
						<span class="order-status-value">
							<span>{{ $t('history.order-summary.status-pending') }}</span>
							<div class="tooltip">
								<v-popover placement="bottom" popover-base-class="v-tooltip v-popover" trigger="click">
									<template slot="popover">
										<div v-close-popover class="tooltip-close" />
									</template>
									<i class="tooltip-button"></i>
									<template slot="popover" class="tooltip-template">
										<i18n path="history.order-summary.status-pending-tooltip" class="text">
											<template v-slot:supportLink>
												<a
													class="link"
													target="_blank"
													href="https://playersupport.nexon.com/hc/en-us?_ga=2.65007915.583200399.1741932959-15862622.1741932959"
													>{{ $t('history.order-summary.nexon-customer-support') }}</a
												>
											</template>
										</i18n>
									</template>
								</v-popover>
							</div>
						</span>
					</div>
				</div>
				<div class="details">
					<span>{{ $t('history.order-summary.order-details') }}</span>
					<div class="header">
						<span class="quantity">{{ $t('history.order-summary.quantity') }}</span>
						<span>{{ $t('history.order-summary.item') }}</span>
						<span>{{ $t('history.order-summary.price') }}</span>
					</div>
					<div v-for="(item, $index) in transactionData.items" :key="$index" class="items">
						<span class="quantity">{{ item.quantity }}</span>
						<span>{{ item.name }}</span>
						<span>
							{{ currencyDisplayFilter(item.price * item.quantity, item.currency) }}
						</span>
					</div>
				</div>
				<div class="breakdown">
					<div v-if="transactionData.taxAmountDisplay" class="sales-tax">
						<span>{{ $t('history.order-summary.sales-tax') }}</span>
						<span>{{ transactionData.taxAmountDisplay }}</span>
					</div>
					<div class="price">
						<span>{{ $t('history.order-summary.price') }}</span>
						<span>{{ transactionData.amountDisplay }}</span>
					</div>
					<div v-if="isUseMileage" class="used-mileage">
						<span>{{ $t('history.order-summary.used-mileage', { currency: mileageName }) }}</span>
						<span class="value" :class="{ warning: isPending }">{{ mileageUnit }}{{ transactionData.mileageAmount }}</span>
					</div>
					<div class="total">
						<span>{{ $t('history.order-summary.total-amount') }}</span>
						<span>{{ transactionData.totalAmountDisplay }}</span>
					</div>
				</div>
			</div>
			<div slot="footer">
				<FormButton
					btn-id="close"
					:btn-primary="true"
					:label="$t('history.order-summary.close')"
					:button-click="close"
					class="orderSummaryCloseBt"
				></FormButton>
			</div>
		</modal>
	</div>
</template>

<script>
import { FormButton } from '@arena/toolkit';
import modal from '@arena/toolkit/src/components/common/modal';

export default {
	name: 'OrderSummary',
	components: {
		modal,
		FormButton,
	},
	props: {
		transactionId: {
			type: String,
			default: '',
		},
		showModal: {
			type: Boolean,
			default: false,
		},
		closeAction: {
			type: Function,
			default: null,
		},
	},
	data() {
		return {
			transactionData: null,
			bodySelector: 'order-summary-body',
		};
	},
	computed: {
		isPending() {
			return this.transactionData && this.transactionData.status === 'pending';
		},
		isUseMileage() {
			return this.transactionData && this.transactionData.mileageAmount > 0;
		},
		isUseMileageAll() {
			return this.isUseMileage && this.transactionData.totalAmount === 0;
		},
		mileageName() {
			return (this.transactionData && this.transactionData.coinName) || '';
		},
		mileageUnit() {
			return (this.transactionData && this.transactionData.unitName) || '';
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.$refs[this.bodySelector].focus();
		});
		this.transactionData = null;
		this.getNonNxTaxInfo();
	},
	methods: {
		close() {
			this.closeAction();
		},
		getNonNxTaxInfo() {
			this.$walletAPI
				.getNonNxTaxInfo(this.transactionId)
				.then(this.setTransactionData)
				.catch(this.errorHandler);
		},
		errorHandler(error) {
			console.log(error);
			this.closeAction();
		},
		setTransactionData({ data }) {
			const currency = data.currency;
			if (data.hasOwnProperty('taxAmount')) {
				data.taxAmountDisplay = this.currencyDisplayFilter(data.taxAmount, currency);
			}
			data.amountDisplay = this.currencyDisplayFilter(data.amount, currency);
			data.totalAmountDisplay = this.currencyDisplayFilter(data.totalAmount, currency);
			this.transactionData = data;
		},
		currencyDisplayFilter(amount, currency) {
			if (currency === 'NX') {
				return `${this.$n(amount)} ${currency}`;
			} else if (currency === 'TWD') {
				return `${this.$n(amount)} ${this.$t('wallet.points')}`;
			} else if (this.$n(amount, 'currency', currency)) {
				return this.$n(amount, 'currency', currency);
			}
			return this.$n(amount);
		},
	},
};
</script>

<style lang="less" scoped>
#commerce {
	.form-select-wrapper {
		margin-top: 0;
		width: 100%;
	}
}
.order-summary {
	@media (min-width: 768px) {
		.order-summary-modal {
			margin-top: -100px;
		}
	}
	.body {
		outline: none;
	}
	.spinner-wrapper {
		align-items: center;
		display: flex;
		height: 150px;
		justify-content: center;
	}
	span:last-child {
		color: #9fbf00;
		float: right;
	}
	.summary {
		padding: 30px 0 25px 0;
		.order-number span:last-child {
			word-wrap: break-word;
		}

		.order-status {
			.order-status-value {
				span {
					color: #e63f57;
				}

				.tooltip {
					position: relative;
					display: inline-block;
					margin-left: 5px;
					font-size: 13px;
					color: #666666;
					opacity: 1;
					top: -1px;
					> .tooltip-button {
						margin-left: 5px;
					}

					.v-popover {
						.tooltip-button {
							display: inline-flex;
							justify-content: center;
							align-items: center;
							border-radius: 50%;
							font-family: Arial;
							font-style: normal;
							font-weight: 700;
							font-size: 8.5px;
							width: 14px;
							height: 14px;
							background-color: #4f5159;
							color: hsla(0, 0%, 100%, 0.8);
							&:before {
								content: '?';
								line-height: 1;
								margin-top: 1px;
							}
							&:hover {
								background-color: #7b7f8c;
								cursor: pointer;
							}
						}

						&.open {
							.tooltip-button {
								background-color: #7b7f8c;
							}
						}
					}
				}
			}
		}
	}
	.details {
		padding-top: 50px;
		.header span {
			color: inherit;
		}
		.items {
			color: #9fbf00;
		}
		> span {
			border-bottom: 1px solid black;
			display: block;
			font-weight: bold;
		}
		div {
			padding: 5px 0;
		}
		.quantity {
			display: inline-block;
			width: 50px;
		}
	}
	.breakdown {
		margin: 25px 0 0 50%;
		.used-mileage {
			.value.warning {
				color: #e63f57;
			}
		}
		.total {
			margin-top: 25px;
			border-top: 1px solid black;
			font-weight: bold;
			padding-top: 30px;
			span:last-child {
				font-size: 20px;
			}
		}
	}
	.orderSummaryCloseBt {
		margin-left: 25%;
		width: 50%;
	}
	@media (max-width: 480px) {
		.summary {
			padding-top: 10px;
			span:last-child {
				display: block;
				float: inherit;
			}
		}
		.breakdown {
			margin-left: 25%;
		}
		.orderSummaryCloseBt {
			margin-left: 0;
			width: 100%;
		}
	}
	@media (max-width: 375px) {
		.breakdown {
			margin-left: 0;
			padding-top: 35px;
		}
	}
}
.mobile,
.launcher {
	.modal {
		position: absolute;
	}
}
</style>
<style lang="scss">
.v-tooltip {
	.wrapper {
		position: absolute;
		right: -6px;
		width: 254px;

		@media (max-width: 480px) {
			left: -50px;
		}

		.text {
			white-space: pre-wrap;
			font-size: 11px;

			.link {
				color: #0077ff;
				text-decoration: underline;
				text-transform: uppercase;
			}
		}
	}
}
</style>
